/**
 * useHomeSummary
 *
 * Loads the most recent jobs for each product on the home page and turns them
 * into per-product summaries. Products load independently, so one failing
 * request shows an error on its own card only. Only the products passed in
 * are requested, so a tenant never triggers a request for a product it is not
 * entitled to.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { JobStatus, JobType, type Job } from '../../graphql/types';
import { listJobsByUser } from '../../services/workflowService';
import type { Product, ProductId } from '../featureLayout/products';
import { buildHomeRow } from './featureHomeModel';
import { buildProductSummary, type ProductSummary } from './homeSummary';
import { mergeJobDetails, useTitleSearchRowDetails } from './useTitleSearchRowDetails';

/** Jobs requested per product. Counts on the cards cover these jobs. */
export const HOME_FETCH_LIMIT = 20;
const MAX_PAGES = 5;

const JOB_TYPE: Readonly<Record<ProductId, JobType>> = {
  DD: JobType.DD,
  BA: JobType.BA,
  TR: JobType.TR,
};

export type CardStatus = 'loading' | 'error' | 'ready';

export interface ProductCardState {
  status: CardStatus;
  summary: ProductSummary | null;
}

interface Loaded {
  status: CardStatus;
  jobs: Job[];
}

/** DynamoDB filters after the page limit, so chain pages until the list is full. */
async function fetchRecentJobs(tenantId: string, username: string, jobType: JobType): Promise<Job[]> {
  let collected: Job[] = [];
  let cursor: string | undefined;
  for (let page = 0; page < MAX_PAGES; page++) {
    const result = await listJobsByUser(tenantId, username, jobType, HOME_FETCH_LIMIT, cursor, false);
    collected = collected.concat(result.items);
    if (!result.nextToken || collected.length >= HOME_FETCH_LIMIT) break;
    cursor = result.nextToken;
  }
  return collected.filter((job) => job.status !== JobStatus.ARCHIVED);
}

export function useHomeSummary(products: readonly Product[]) {
  const [loaded, setLoaded] = useState<Partial<Record<ProductId, Loaded>>>({});
  const [reloadKey, setReloadKey] = useState(0);

  const productKey = products.map((p) => p.id).join(',');

  useEffect(() => {
    if (!productKey) return;
    const ids = productKey.split(',') as ProductId[];
    let cancelled = false;
    setLoaded(Object.fromEntries(ids.map((id) => [id, { status: 'loading', jobs: [] }])));

    void (async () => {
      let tenantId = 'default-tenant';
      let username = 'unknown';
      try {
        const session = await fetchAuthSession();
        const payload = session.tokens?.idToken?.payload;
        tenantId = (payload?.['custom:tenantId'] as string) || tenantId;
        username = (payload?.['cognito:username'] as string) || username;
      } catch (err) {
        console.error('Failed to read session for home summary:', err);
      }
      await Promise.all(
        ids.map(async (id) => {
          let next: Loaded;
          try {
            next = { status: 'ready', jobs: await fetchRecentJobs(tenantId, username, JOB_TYPE[id]) };
          } catch (err) {
            console.error(`Failed to load ${id} jobs for home:`, err);
            next = { status: 'error', jobs: [] };
          }
          if (!cancelled) setLoaded((prev) => ({ ...prev, [id]: next }));
        })
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [productKey, reloadKey]);

  // TR jobs arrive without their stage fields; fill them like the Title Search home does.
  const trJobs = loaded.TR?.jobs ?? [];
  const trDetails = useTitleSearchRowDetails(trJobs, trJobs.length > 0);

  const cards = useMemo(() => {
    const now = new Date();
    const result: Partial<Record<ProductId, ProductCardState>> = {};
    for (const product of products) {
      const entry = loaded[product.id];
      if (!entry || entry.status === 'loading') {
        result[product.id] = { status: 'loading', summary: null };
      } else if (entry.status === 'error') {
        result[product.id] = { status: 'error', summary: null };
      } else {
        const rows = entry.jobs.map((job) => {
          const extra = product.id === 'TR' ? trDetails.get(job.jobId) : undefined;
          const effective = extra?.job ? mergeJobDetails(job, extra.job) : job;
          return buildHomeRow(effective, extra?.grade ?? null);
        });
        result[product.id] = { status: 'ready', summary: buildProductSummary(product, rows, now) };
      }
    }
    return result;
  }, [products, loaded, trDetails]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  return { cards, reload };
}
